/**
 * Weight Field Normalization Utilities
 * 
 * DRY utilities for normalizing rich weight fields across different data contexts
 */

import has from 'lodash/has'
import { extractDisplayWeight } from './weightUtils'
import { getDisplayUnit } from './weightDisplay'

/**
 * Standard weight field names that commonly need normalization
 */
export const COMMON_WEIGHT_FIELDS = [
  'grossWeight',
  'receivedWeight',
  'processWeight',
  'meltLoss',
  'projectedGrossWeight',
  'aggregatedGrossWeight',
  'aggregatedMeltLoss',
  'aggregatedPinWeight'
]

/**
 * Normalize specific weight fields on an object using context-appropriate units
 * @param {object} obj - Object containing weight fields
 * @param {Array<string>} weightFields - Array of weight field names to normalize
 * @param {string} context - Display context ('lot', 'fire', 'bar', etc.)
 * @param {object} options - Additional options
 * @returns {object} Object with normalized weight fields
 */
export const normalizeWeightFields = (obj, weightFields, context = 'lot', options = {}) => {
  if (!obj) return obj
  
  const { preserveOriginal = false } = options
  const { unit: displayUnit } = getDisplayUnit(context)
  const normalized = preserveOriginal ? { ...obj } : obj
  
  weightFields.forEach(fieldName => {
    if (has(obj, fieldName)) {
      normalized[fieldName] = extractDisplayWeight(obj[fieldName], displayUnit)
    }
  })
  
  return normalized
}

/**
 * Normalize weight fields for lot-level data (refining lots, assays)
 * @param {object} lotData - Lot data object
 * @param {object} options - Options
 * @returns {object} Normalized lot data
 */
export const normalizeLotWeights = (lotData, options = {}) => {
  if (!lotData) return lotData
  
  const lotWeightFields = [
    'receivedWeight',
    'projectedGrossWeight', 
    'aggregatedGrossWeight',
    'aggregatedMeltLoss',
    'aggregatedPinWeight'
  ]
  
  return normalizeWeightFields(lotData, lotWeightFields, 'lot', options)
}

/**
 * Normalize weight fields for assay-level data
 * @param {object} assayData - Assay data object
 * @param {object} options - Options
 * @returns {object} Normalized assay data
 */
export const normalizeAssayWeights = (assayData, options = {}) => {
  if (!assayData) return assayData
  
  const assayWeightFields = [
    'grossWeight',
    'pinWeight', 
    'meltLoss'
  ]
  
  return normalizeWeightFields(assayData, assayWeightFields, 'lot', options)
}

/**
 * Normalize weight fields for bar-level data
 * Bars own grossWeight only; pin weights live on distinct pin rows
 * @param {object} barData - Bar data object
 * @param {object} options - Options
 * @returns {object} Normalized bar data
 */
export const normalizeBarWeights = (barData, options = {}) => {
  if (!barData) return barData

  const barWeightFields = [
    'grossWeight'
  ]

  return normalizeWeightFields(barData, barWeightFields, 'bar', options)
}

/**
 * Normalize weight fields for distinct pin rows
 * @param {object} pinData - Pin data object
 * @param {object} options - Options
 * @returns {object} Normalized pin data
 */
export const normalizePinWeights = (pinData, options = {}) => {
  if (!pinData) return pinData

  const pinWeightFields = [
    'grossWeight',
    'assayLoss'
  ]

  return normalizeWeightFields(pinData, pinWeightFields, 'pin', options)
}

/**
 * Normalize weight fields for fire assay data (cups, dore)
 * @param {object} fireData - Fire assay data object  
 * @param {object} options - Options
 * @returns {object} Normalized fire assay data
 */
export const normalizeFireWeights = (fireData, options = {}) => {
  if (!fireData) return fireData
  
  const fireWeightFields = [
    'grossWeight',
    'netWeight'
  ]
  
  return normalizeWeightFields(fireData, fireWeightFields, 'fire', options)
}

/**
 * Batch normalize an array of objects
 * @param {Array<object>} objects - Array of objects to normalize
 * @param {function} normalizer - Normalization function to apply
 * @param {object} options - Options to pass to normalizer
 * @returns {Array<object>} Normalized objects
 */
export const normalizeWeightArray = (objects, normalizer, options = {}) => {
  if (!Array.isArray(objects)) return objects
  
  return objects.map(obj => normalizer(obj, options))
}
